import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import VoltieUser from '../../types/VoltieUser'
import { Badge, Card, CardTitle } from './userStyles'

// Read-only support view of a user's per-charger push settings (VLT-2554). The apps write
// preferences/charger_push_notifications/<chargerId> = { muted, overrides: { <category>: bool } }
// (VLT-2549); every cell shows what that charger actually sends, by the backend's rule (VLT-2544,
// ChargerPushSettings.wantsChargerPush): muted sends nothing, an override wins, anything else
// follows the account setting. The statistics email and the newsletter are account-wide only.

type ChargerPushKey =
  | 'car_plugged_in' | 'car_unplugged' | 'charger_not_available' | 'charger_errors' | 'charger_software_update'
  | 'charging_started' | 'charging_finished' | 'charging_not_started' | 'scheduled_charging_started' | 'limited_charging_started'
  | 'car_not_charging' | 'car_battery_level_reached'

const GROUPS: { titleKey: string, keys: ChargerPushKey[] }[] = [
  { titleKey: 'user_pushGroupCharger', keys: ['car_plugged_in', 'car_unplugged', 'charger_not_available', 'charger_errors', 'charger_software_update'] },
  { titleKey: 'user_pushGroupCharging', keys: ['charging_started', 'charging_finished', 'charging_not_started', 'scheduled_charging_started', 'limited_charging_started'] },
  { titleKey: 'user_pushGroupVehicle', keys: ['car_not_charging', 'car_battery_level_reached'] },
]

interface ChargerPushOverride { muted: boolean, overrides: Partial<Record<ChargerPushKey, boolean>> }

// The backend's reading: no push_notifications at all means nothing is wanted, a missing category is
// off, except charging_not_started, which came later and is on until switched off.
function accountWants(user: VoltieUser, key: ChargerPushKey): boolean {
  const push = user.preferences?.push_notifications
  if (!push) return false
  if (key === 'charging_not_started') return push.charging_not_started !== false
  return push[key] === true
}

// Anything that is not an object counts as not set; only true mutes, and only booleans override.
function parseOverrides(raw: unknown): { [chargerId: string]: ChargerPushOverride } {
  if (!raw || typeof raw !== 'object') return {}
  const result: { [chargerId: string]: ChargerPushOverride } = {}
  for (const [chargerId, entry] of Object.entries(raw as { [key: string]: unknown })) {
    if (!entry || typeof entry !== 'object') continue
    const { muted, overrides } = entry as { muted?: unknown, overrides?: unknown }
    const parsed: ChargerPushOverride = { muted: muted === true, overrides: {} }
    if (overrides && typeof overrides === 'object') {
      for (const [key, value] of Object.entries(overrides)) {
        if (typeof value === 'boolean') parsed.overrides[key as ChargerPushKey] = value
      }
    }
    result[chargerId] = parsed
  }
  return result
}

const TableScroll = styled.div`
  overflow-x: auto;
  margin: 0px -4px;
`

const Table = styled.table`
  border-collapse: collapse;
  min-width: 100%;
  font-size: 13px;
  color: ${props => props.theme.colors.gray_900};

  th, td {
    padding: 8px 12px;
    text-align: center;
    vertical-align: middle;
  }

  th:first-child, td:first-child {
    text-align: left;
    padding-left: 4px;
    min-width: 180px;
  }

  tbody td {
    border-top: 1px solid ${props => props.theme.colors.gray_200};
  }
`

const ChargerHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  min-width: 120px;
`

const ChargerName = styled.span`
  font-weight: 700;
  font-size: 13px;
`

const ChargerId = styled.span`
  font-family: monospace;
  font-size: 11px;
  font-weight: 500;
  color: ${props => props.theme.colors.gray_500};
`

const ColumnTitle = styled.span`
  font-weight: 700;
  font-size: 13px;
  color: ${props => props.theme.colors.gray_500};
`

const GroupRow = styled.td`
  font-weight: 700;
  font-size: 12px;
  color: ${props => props.theme.colors.gray_500};
  padding-top: 16px !important;
`

const Inherited = styled.span`
  color: ${props => props.theme.colors.gray_500};
  font-weight: 600;
`

const Note = styled.p`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0px 0px;
  font-size: 13px;
  color: ${props => props.theme.colors.gray_500};
`

export default function UserChargerPushSettings({ user }: { user: VoltieUser }) {
  const { t } = useTranslation()

  const paired = user.chargers || {}
  const settings = parseOverrides(user.preferences?.charger_push_notifications)
  const customized = Object.entries(settings)
    .filter(([, setting]) => setting.muted || Object.keys(setting.overrides).length > 0)
    .map(([chargerId, setting]) => ({ chargerId, setting, name: paired[chargerId]?.name || paired[chargerId]?.hostname || '', isPaired: chargerId in paired }))
    .sort((a, b) => Number(b.isPaired) - Number(a.isPaired) || (a.name || a.chargerId).localeCompare(b.name || b.chargerId))
  const hasDefaultChargers = Object.keys(paired).some(chargerId => !customized.some(entry => entry.chargerId === chargerId))
  const onOff = (value: boolean) => t(value ? 'general_on' : 'general_off')

  return (
    <Card>
      <CardTitle>{t('user_notificationsPerCharger')}</CardTitle>
      {customized.length === 0 ? (
        <Note style={{ marginTop: 0 }}>{t('user_allChargersUseTheDefaultSettings')}</Note>
      ) : <>
        <TableScroll>
          <Table>
            <thead>
              <tr>
                <th />
                <th><ColumnTitle>{t('user_default')}</ColumnTitle></th>
                {customized.map(({ chargerId, setting, name, isPaired }) => (
                  <th key={chargerId}>
                    <ChargerHeader>
                      {name && <ChargerName>{name}</ChargerName>}
                      <ChargerId>{chargerId}</ChargerId>
                      {setting.muted
                        ? <Badge $tone="warning">{t('user_muted')}</Badge>
                        : <Badge $tone="primary">{t('user_custom')}</Badge>}
                      {!isPaired && <Badge $tone="neutral">{t('user_noLongerPaired')}</Badge>}
                    </ChargerHeader>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {GROUPS.map(group => [
                <tr key={group.titleKey}><GroupRow colSpan={customized.length + 2}>{t(group.titleKey)}</GroupRow></tr>,
                ...group.keys.map(key => {
                  const account = accountWants(user, key)
                  return (
                    <tr key={key}>
                      <td>{t(`user_push_${key}`)}</td>
                      <td><Inherited>{onOff(account)}</Inherited></td>
                      {customized.map(({ chargerId, setting }) => {
                        const override = setting.overrides[key]
                        if (setting.muted) return <td key={chargerId}><Inherited>{onOff(false)}</Inherited></td>
                        if (override !== undefined) return <td key={chargerId}><Badge $tone="primary">{onOff(override)}</Badge></td>
                        return <td key={chargerId}><Inherited>{onOff(account)}</Inherited></td>
                      })}
                    </tr>
                  )
                }),
              ])}
            </tbody>
          </Table>
        </TableScroll>
        <Note>
          <Badge $tone="primary">{t('general_on')}</Badge>{t('user_setForThisCharger')}
        </Note>
        {hasDefaultChargers && <Note>{t('user_everyOtherChargerUsesTheDefaultSettings')}</Note>}
      </>}
    </Card>
  )
}
